import React, { useEffect, useRef, useState } from 'react';
import {
  Space,
  Row,
  Modal,
  Form,
  notification,
  Typography,
  Button,
  Input
} from 'antd';
import EmailTemplatesTable from './components/EmailTemplatesTable';
import EmailModal from './components/EmailModal';
import EmailTemplateFilters from './components/EmailTemplateFilters';
import EmailAddModal from './components/EmailAddModal';
import { getCourses } from '../../services/courseService';
import { getCourseOffers } from '../../services/courseOfferService';
import { getCourseEnrolments } from '../../services/courseEnrolmentService';
import {
  getEmailTemplates,
  sendAllEmailData
} from '../../services/emailTemplateService';

const { Text, Title } = Typography;

const PAGE_SIZE = 20;
const DEFAULT_NOTIFICATION_DURATION = 5;

const filterItems = (value, originalData, setter) => {
  const filtered = originalData.filter((item) => {
    const substring = item.label.substr(0, value.length);
    return substring.toLowerCase() === value.toLowerCase();
  });
  setter(filtered);
};

const EmailTemplates = () => {
  const totalCount = useRef(null);
  const [pagination, setPagination] = useState({
    pageNo: 1,
    pageSize: PAGE_SIZE
  });
  const [offset, setOffset] = useState(
    (pagination.pageNo - 1) * pagination.pageSize
  );

  const [confirmationModalOpened, setConfirmationModalOpened] = useState(
    false
  );
  const [fetching, setFetching] = useState(false);

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);

  const [formData, setFormData] = useState({ learner: [] });
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [courseOffers, setCourseOffers] = useState([]);
  const [filteredCourseOffers, setFilteredCourseOffers] = useState([]);
  const [learners, setLearners] = useState([]);
  const [filteredLearners, setFilteredLearners] = useState([]);
  const [sendEmailToAllInCourse, setSendEmailToAllInCourse] = useState(
    false
  );
  const [statusList, setStatusList] = useState(['Pending', 'Current']);
  const [searchParams, setSearchParams] = useState({});
  const [visibleAddModal, setVisibleAddModal] = useState(false);

  const fetchData = (pageNo, pageSize, param) => {
    setFetching(true);
    getEmailTemplates(pageNo, pageSize, param)
      .then((response) => {
        totalCount.current = response.data.templates?.totalCount;
        setEmailTemplates(response.data.templates?.paginatedList);
      })
      .catch((error) => {
        console.log('Error getting data from the admin backend:', error);
        alert('Getting data from the backend failed!');
      })
      .finally(() => {
        setFetching(false);
      });
  };

  useEffect(() => {
    if (searchParams) {
      fetchData(pagination.pageNo, pagination.pageSize, searchParams);
    } else {
      fetchData(pagination.pageNo, pagination.pageSize);
    }
  }, [pagination, searchParams]);

  useEffect(() => {
    setFilteredCourses(courses);
  }, [courses]);

  useEffect(() => {
    setFilteredCourseOffers(courseOffers);
  }, [courseOffers]);

  useEffect(() => {
    setFilteredLearners(learners);
  }, [learners]);

  const clearAll = () => {
    setCourses([]);
    setCourses([]);
    setLearners([]);
    setFormData({ learner: [] });
    setActiveTemplate(null);
    setSendEmailToAllInCourse(false);
  };

  const onPaginationChange = (data) => {
    setPagination({
      pageNo: data.current,
      pageSize: data.pageSize
    });
    setOffset((data.current - 1) * data.pageSize);
    if (searchParams) {
      fetchData(data.current, data.pageSize, searchParams);
    } else {
      fetchData(data.current, data.pageSize);
    }
  };

  const [form] = Form.useForm();

  const onSearch = (data) => {
    setSearchParams({ [data.param]: data.val });
    fetchData(1, PAGE_SIZE, { [data.param]: data.val.toUpperCase() });
  };

  const onClickAdd = () => {
    setVisibleAddModal(true);
  };

  const onClickCancel = () => {
    setVisibleAddModal(false);
  };

  const onReset = () => {
    form.resetFields();
    setOffset(0);
    fetchData(1, PAGE_SIZE);
  };

  const coursesProps = {
    fetch: () => {
      setFetching(true);
      getCourses(0, 0, {})
        .then((res) => {
          const data = res.data.paginatedList.map((course) => ({
            label: course.code,
            value: course.code,
            title: course.title
          }));
          setCourses(data);
          setCourseOffers([]);
          setLearners([]);
        })
        .catch((error) => {
          console.log(
            'Error on fetching courses data from the admin backend:',
            error
          );
          alert('Fetching courses data from the backend failed!');
        })
        .finally(() => {
          setFetching(false);
        });
    },
    filter: (value) => {
      filterItems(value, courses, setFilteredCourses);
    },
    onChange: (value) => {
      const course = courses.find((course) => course.value === value);
      setFormData({ course, learner: [] });
      getCourseOffers(0, 0, { courseCode: value })
        .then((res) => {
          const data = res.data.paginatedList.map((courseOffer) => ({
            label: courseOffer.code,
            value: courseOffer.courseOfferId
          }));
          setCourseOffers(data);
          setLearners([]);
        })
        .catch((error) => {
          console.log(
            'Error on fetching course offers data from the admin backend:',
            error
          );
          alert('Fetching course offers data from the backend failed!');
        })
        .finally(() => {
          setFetching(false);
        });
    },
    data: filteredCourses
  };
  const courseOffersProps = {
    filter: (value) => {
      filterItems(value, courseOffers, setFilteredCourseOffers);
    },
    onChange: (value) => {
      const courseOffer = courseOffers.find(
        (offer) => offer.value === value
      );
      setFormData(
        Object.assign({}, formData, { courseOffer, learner: [] })
      );
      getCourseEnrolments(0, 0, { courseOfferId: courseOffer.value })
        .then((res) => {
          const data = res.data.paginatedList.map((i) => {
            const name = i.learner
              ? `${i.learner.firstName} ${i.learner.lastName}`
              : '';

            const label = `${name} (${i.learner.email})`;

            return {
              ...i,
              label,
              name,
              value: i.learner.learnerId
            };
          });
          setLearners(data);
        })
        .catch((error) => {
          console.log(
            'Error on fetching data from the admin backend:',
            error
          );
          alert('Fetching course offers data from the backend failed!');
        })
        .finally(() => {
          setFetching(false);
        });
    },
    data: filteredCourseOffers
  };

  const learnersProps = {
    filter: (value) => {
      filterItems(value, learners, setFilteredLearners);
    },
    onChange: (valueArr) => {
      const learner = learners.filter((learner) =>
        valueArr.includes(learner.value)
      );
      setFormData({
        ...formData,
        learner
      });
    },
    data: filteredLearners
  };

  const isFormDataMissing = () =>
    !formData.course || !formData.courseOffer || !formData.learner;

  const handleOnConfirm = () => {
    if (isFormDataMissing()) return;
    setConfirmationModalOpened(true);
  };

  const handleEmailSending = async () => {
    if (isFormDataMissing()) return;
    let emailBodyList;

    if (sendEmailToAllInCourse) {
      const filterLearnersByStatus = learners.filter((learner) =>
        statusList.includes(learner.courseEnrolmentStatus)
      );
      emailBodyList = filterLearnersByStatus.map((learner) => {
        return {
          student_name: learner.name,
          learner_id: learner.learnerId,
          course_enrolment_id: learner.courseEnrolmentId,
          course_id: learner.courseOffer.courseId,
          course_offer_id: learner.courseOfferId,
          course_code: learner.courseOffer.code,
          course_name: learner.courseOffer.courseDescription,
          start_date: learner.courseOffer.startDate,
          end_date: learner.courseOffer.endDate,
          additional_details: learner.courseOffer.generalNotes,
          country: learner.learner.countryId,
          userRole: 'learner'
        };
      });
    } else {
      emailBodyList = formData.learner.map((learner) => {
        return {
          student_name: learner.name,
          learner_id: learner.learnerId,
          course_enrolment_id: learner.courseEnrolmentId,
          course_id: learner.courseOffer.courseId,
          course_offer_id: learner.courseOfferId,
          course_code: learner.courseOffer.code,
          course_name: learner.courseOffer.courseDescription,
          start_date: learner.courseOffer.startDate,
          end_date: learner.courseOffer.endDate,
          additional_details: learner.courseOffer.generalNotes,
          country: learner.learner.countryId,
          userRole: 'learner'
        };
      });
    }

    setFetching(true);
    const noOfEmails = emailBodyList.length;
    const request = {
      mailType: activeTemplate.mailType,
      mailCourse: formData.course.value,
      mailCourseOffer: formData.courseOffer.label,
      toMail: [],
      toMailName: [],
      data: emailBodyList
    };
    if (noOfEmails >= 30) {
      openNotificationWithIcon(
        'warning',
        'DO NOT CLOSE WINDOW!',
        `Wow! You are sending ${noOfEmails} emails. 
          It may take a while. We will notify you when it is complete.`,
        0
      );
    } else {
      openNotificationWithIcon(
        'warning',
        'DO NOT CLOSE WINDOW!',
        `Your email template is on the way. 
          We will notify you when it is complete soon.`,
        0
      );
    }
    await sendAllEmailData(request)
      .then((res) => {
        if (res.status === 201) {
          openNotificationWithIcon(
            'success',
            'Email template sent!',
            handleResponse(res.data),
            0 // Never close automatically
          );
        }
        setConfirmationModalOpened(false);
        clearAll();
      })
      .catch((error) => {
        openNotificationWithIcon(
          'error',
          'Email template is NOT sent!',
          `Failed to send email template to ${noOfEmails} ${
            noOfEmails > 1 ? 'students' : 'student'
          }`,
          DEFAULT_NOTIFICATION_DURATION
        );
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const handleResponse = (response) => {
    const errorList = [];
    const successList = [];
    response.forEach((res) => {
      if (res.status === 'Mail Sent Successfully') {
        successList.push(res.data.student_name);
      } else {
        errorList.push(
          `${res.data.student_name} (${res.data.learnerData.email}`
        );
      }
    });
    const successListLength = successList.length;
    let msg = `Email template is successfully sent to ${successListLength} ${
      successListLength > 1 ? 'learners' : 'learner'
    }.`;
    const errorListLength = errorList.length;
    if (errorListLength > 0) {
      msg.concat(
        `Failed to send to ${errorListLength} ${
          errorListLength > 1 ? 'learners' : 'learner'
        }: ${errorList.join(', ')}`
      );
    }
    return msg;
  };

  const openNotificationWithIcon = (type, title, msg, duration) => {
    notification[type]({
      message: title,
      description: msg,
      duration
    });
  };

  return (
    <div>
      {visibleAddModal && (
        <EmailAddModal
          visible={'visibleAddModal'}
          onCancel={onClickCancel}></EmailAddModal>
      )}
      {confirmationModalOpened && (
        <Modal
          onOk={handleEmailSending}
          onCancel={() => setConfirmationModalOpened(false)}
          visible={true}
          title="Are you sure you want to send the email?"
          centered={true}
          okText="Send Email"
          confirmLoading={fetching}
        />
      )}
      {activeTemplate !== null && (
        <EmailModal
          name={activeTemplate.mailType}
          subject={activeTemplate.mailSubject}
          content={activeTemplate.mailContentLink}
          onClose={clearAll}
          onConfirm={handleOnConfirm}
          fetching={fetching}
          courses={coursesProps}
          selectedCourseOffer={
            formData.courseOffer ? formData.courseOffer.value : null
          }
          courseOffers={courseOffersProps}
          selectedLearner={
            formData.learner.length
              ? formData.learner.map((i) => i.value)
              : []
          }
          learners={learnersProps}
          hasAttachment={activeTemplate.hasMailAttachment}
          sendEmailToAllInCourse={sendEmailToAllInCourse}
          setSendEmailToAllInCourse={(value) =>
            setSendEmailToAllInCourse(value)
          }
          setStatusList={(value) => setStatusList(value)}
          statusList={statusList}
        />
      )}
      <Space size="large">
        <h1>Email Templates</h1>
      </Space>
      <Space size="large">
        <Button type="ghost" onClick={onClickAdd}>
          Add
        </Button>
      </Space>
      <Row justify="space-between">
        <h3>Total {totalCount.current} Record(s)</h3>
      </Row>
      <EmailTemplateFilters
        form={form}
        onSearch={onSearch}
        onReset={onReset}
        initialValues={{ param: 'mailType' }}
      />
      <EmailTemplatesTable
        data={emailTemplates}
        onPaginationChange={onPaginationChange}
        offset={offset}
        loading={fetching}
        totalCount={totalCount.current}
        onTemplateSelect={setActiveTemplate}
      />
    </div>
  );
};

export default EmailTemplates;
